/**
 * A restaurant's plan, from the platform's side: move it to any plan, and
 * give, extend or end a free trial.
 *
 * Each sheet says what will happen before it happens — what the restaurant
 * loses on a move down, what it goes back to when a trial ends, who is
 * emailed — because every one of them reaches the restaurant's owners.
 * What a plan includes comes from the server (`usePlatformPlanChoices`);
 * the screen only compares the lists. Amounts are checked as text; the
 * server prices.
 */

import { useState } from 'react';
import {
  displayLocale,
  formatMoney,
  useChangeRestaurantPlan,
  useEndTrial,
  useExtendTrial,
  useStartTrial,
  type Mailed,
  type PlanChoice,
  type PlanChoices,
  type PlatformTrial,
} from '@restaurant/api-client';
import { ActionError, Button, Field, Input, Segmented, Sheet } from '@restaurant/ui';

import { addDays, cycleWords, dayOf, featuresLost, formatDay, isAmount } from './words';

type Cycle = 'month' | 'year';

interface Restaurant {
  id: string;
  name: string;
}

function mailedWords(mailed: Mailed): string {
  const sent = mailed.sent_to.length
    ? ` Emailed to ${mailed.sent_to.join(', ')}.`
    : ' Nobody was emailed: the restaurant has no owner with a login.';
  const failed = mailed.failed.length
    ? ` Could not reach ${mailed.failed.join(', ')}; tell them another way.`
    : '';
  return sent + failed;
}

function priceOf(plan: PlanChoice | undefined, cycle: Cycle): string | null {
  if (!plan) return null;
  return cycle === 'year' ? plan.price_year : plan.price_month;
}

function LostList({
  heading,
  keys,
  names,
}: {
  heading: string;
  keys: string[];
  names: Record<string, string>;
}) {
  if (keys.length === 0) return null;
  return (
    <div className="pf__lost" role="status">
      <p className="pf__warn">{heading}</p>
      <ul className="pf__bullets">
        {keys.map((key) => (
          <li key={key}>{names[key] ?? key}</li>
        ))}
      </ul>
      <p className="pf__faint">
        Nothing is deleted: what they made stays readable, and it takes changes again if they
        move back up.
      </p>
    </div>
  );
}

function PlanSelect({
  plans,
  value,
  onChange,
  label = 'Plan',
}: {
  plans: PlanChoice[];
  value: string;
  onChange: (key: string) => void;
  label?: string;
}) {
  return (
    <Field label={label}>
      <select className="ui-input" value={value} onChange={(e) => onChange(e.target.value)}>
        {plans.map((plan) => (
          <option key={plan.key} value={plan.key}>
            {plan.name}
          </option>
        ))}
      </select>
    </Field>
  );
}

// --------------------------------------------------------------------------- //
// Change plan (also "Convert to paid" during a trial)
// --------------------------------------------------------------------------- //

export function ChangePlanSheet({
  restaurant,
  choices,
  cycle: currentCycle,
  trial,
  startWith,
  onDismiss,
}: {
  restaurant: Restaurant;
  choices: PlanChoices;
  /** How it is billed now. */
  cycle: Cycle;
  trial: PlatformTrial | null;
  /** The plan to open on: the trial's, for "Convert to paid". */
  startWith?: string | undefined;
  onDismiss: () => void;
}) {
  const change = useChangeRestaurantPlan();
  const locale = displayLocale();
  const firstOther = choices.options.find((o) => o.key !== choices.current)?.key ?? '';
  const [planKey, setPlanKey] = useState(startWith ?? firstOther);
  const [cycle, setCycle] = useState<Cycle>(currentCycle);
  const [invoice, setInvoice] = useState(true);
  const [amount, setAmount] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');

  const plan = choices.options.find((o) => o.key === planKey);
  const paying = choices.options.find((o) => o.key === choices.paying);
  const listPrice = priceOf(plan, cycle);
  // The list price until the operator types something else.
  const typed = amount ?? listPrice ?? '';
  const needsAmount = invoice && listPrice === null;
  const amountOk = !invoice || (typed.trim() === '' ? !needsAmount : isAmount(typed));
  const unchanged = trial === null && planKey === choices.current && cycle === currentCycle;
  const lost = plan && paying ? featuresLost(paying.features, plan.features) : [];
  const ready = plan !== undefined && !unchanged && amountOk && reason.trim() !== '';

  if (change.data) {
    const issued = change.data.invoice;
    return (
      <Sheet
        title="Plan changed"
        onDismiss={onDismiss}
        actions={
          <Button variant="primary" size="lg" block onClick={onDismiss}>
            Done
          </Button>
        }
      >
        <p className="pf__sub">
          {restaurant.name} is on {plan?.name}, billed {cycleWords(cycle)}, from today.
          {change.data.ended_trial ? ' Its free trial is over: it is paying now.' : ''}
          {change.data.answered_request ? ' The request it was waiting on is approved.' : ''}
          {issued
            ? ` Invoice ${issued.number} for ${formatMoney(issued.amount, issued.currency, locale)} is due ${formatDay(issued.due_on, locale)}.`
            : ' No invoice was issued.'}
          {mailedWords(change.data.mailed)}
        </p>
      </Sheet>
    );
  }

  return (
    <Sheet
      title={trial ? `Convert ${restaurant.name} to paid` : `Change ${restaurant.name}'s plan`}
      onDismiss={onDismiss}
      actions={
        <>
          <Button size="lg" block onClick={onDismiss}>
            Not now
          </Button>
          <Button
            variant="primary"
            size="lg"
            block
            disabled={!ready}
            loading={change.isPending}
            onClick={() =>
              change.mutate({
                restaurantId: restaurant.id,
                plan: planKey,
                cycle,
                invoice,
                amount: invoice && typed.trim() !== '' ? typed.trim() : null,
                note: note.trim(),
                reason: reason.trim(),
              })
            }
          >
            Change the plan
          </Button>
        </>
      }
    >
      <p className="pf__sub">
        The plan changes today, with no request from the restaurant. The owners are emailed what
        changed{invoice ? ', with the invoice' : ''}.
        {trial
          ? ` This ends the free trial of ${trial.plan.name}: the restaurant pays from today, and does not go back to ${trial.previous_plan.name}.`
          : ''}
      </p>
      <PlanSelect
        plans={choices.options}
        value={planKey}
        onChange={(key) => {
          setPlanKey(key);
          setAmount(null);
        }}
      />
      <Segmented
        label="Billing cycle"
        value={cycle}
        onChange={(value) => {
          setCycle(value);
          setAmount(null);
        }}
        options={[
          { value: 'month', label: 'Monthly' },
          { value: 'year', label: 'Yearly' },
        ]}
      />
      {unchanged && <p className="pf__warn">It is already on {plan?.name}, billed that way.</p>}
      <LostList
        heading={`Moving to ${plan?.name ?? 'this plan'}, ${restaurant.name} no longer has:`}
        keys={lost}
        names={choices.feature_names}
      />
      <label className="pf__check">
        <input
          type="checkbox"
          checked={invoice}
          onChange={(e) => setInvoice(e.target.checked)}
        />
        Issue an invoice for the new period and email it to the owners
      </label>
      {invoice && (
        <Field
          label={`Amount (${choices.currency})`}
          hint={
            listPrice === null
              ? 'This plan has no list price. Type the price you agreed.'
              : `The list price is ${formatMoney(listPrice, choices.currency, locale)}. Change it for an agreed price.`
          }
          error={
            typed.trim() !== '' && !isAmount(typed)
              ? 'Type an amount like 59 or 59.50.'
              : undefined
          }
        >
          <Input
            value={typed}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
          />
        </Field>
      )}
      <Field
        label="Note to the restaurant"
        hint="Optional. In their email and their audit log."
      >
        <Input value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <Field label="Reason" hint="For the platform's log. The restaurant does not see it.">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <ActionError error={change.error} action="Changing the plan" />
    </Sheet>
  );
}

// --------------------------------------------------------------------------- //
// Free trials
// --------------------------------------------------------------------------- //

export function StartTrialSheet({
  restaurant,
  choices,
  cycle,
  onDismiss,
}: {
  restaurant: Restaurant;
  choices: PlanChoices;
  cycle: Cycle;
  onDismiss: () => void;
}) {
  const start = useStartTrial();
  const locale = displayLocale();
  const current = choices.options.find((o) => o.key === choices.current);
  const above = choices.options.filter((o) => o.tier > (current?.tier ?? 0));
  const [planKey, setPlanKey] = useState(above[0]?.key ?? '');
  const [days, setDays] = useState('14');
  const [note, setNote] = useState('');
  const count = /^\d{1,3}$/.test(days.trim()) ? Number(days) : NaN;
  const daysOk = count >= 1 && count <= choices.trial_max_days;
  const plan = above.find((o) => o.key === planKey);
  const today = new Date().toISOString().slice(0, 10);

  if (start.data) {
    const trial = start.data.trial;
    return (
      <Sheet
        title="Free trial started"
        onDismiss={onDismiss}
        actions={
          <Button variant="primary" size="lg" block onClick={onDismiss}>
            Done
          </Button>
        }
      >
        <p className="pf__sub">
          {restaurant.name} is on {trial.plan.name} until{' '}
          {formatDay(dayOf(trial.ends_at), locale)}, then back to {trial.previous_plan.name} by
          itself.{mailedWords(start.data.mailed)}
        </p>
      </Sheet>
    );
  }

  return (
    <Sheet
      title={`A free trial for ${restaurant.name}`}
      onDismiss={onDismiss}
      actions={
        <>
          <Button size="lg" block onClick={onDismiss}>
            Not now
          </Button>
          <Button
            variant="primary"
            size="lg"
            block
            disabled={!plan || !daysOk}
            loading={start.isPending}
            onClick={() =>
              start.mutate({
                restaurantId: restaurant.id,
                plan: planKey,
                days: count,
                note: note.trim(),
              })
            }
          >
            Start the trial
          </Button>
        </>
      }
    >
      {above.length === 0 ? (
        <p className="pf__sub">
          {restaurant.name} is on the top plan already: there is nothing higher to try.
        </p>
      ) : (
        <>
          <p className="pf__sub">
            Free, with no invoice. When it ends, {restaurant.name} goes back to {current?.name},
            billed {cycleWords(cycle)}, by itself — unless you change its plan before then. The
            owners are emailed now, three days before the end, and when it ends.
          </p>
          <PlanSelect plans={above} value={planKey} onChange={setPlanKey} label="Plan to try" />
          <Field
            label="Days"
            hint={
              daysOk
                ? `Until ${formatDay(addDays(today, count), locale)}.`
                : `From 1 to ${choices.trial_max_days}.`
            }
            error={
              days.trim() !== '' && !daysOk ? `From 1 to ${choices.trial_max_days}.` : undefined
            }
          >
            <Input value={days} onChange={(e) => setDays(e.target.value)} inputMode="numeric" />
          </Field>
          <Field label="Note to the restaurant" hint="Optional. In their email.">
            <Input value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </>
      )}
      <ActionError error={start.error} action="Starting the trial" />
    </Sheet>
  );
}

export function ExtendTrialSheet({
  restaurant,
  trial,
  maxDays,
  onDismiss,
}: {
  restaurant: Restaurant;
  trial: PlatformTrial;
  maxDays: number;
  onDismiss: () => void;
}) {
  const extend = useExtendTrial();
  const locale = displayLocale();
  const endsOn = dayOf(trial.ends_at);
  const earliest = addDays(endsOn, 1);
  const latest = addDays(new Date().toISOString().slice(0, 10), maxDays);
  const [day, setDay] = useState(addDays(endsOn, 7) > latest ? latest : addDays(endsOn, 7));
  const [reason, setReason] = useState('');
  const dayOk = day >= earliest && day <= latest;

  return (
    <Sheet
      title={`Extend ${restaurant.name}'s free trial`}
      onDismiss={onDismiss}
      actions={
        <>
          <Button size="lg" block onClick={onDismiss}>
            Not now
          </Button>
          <Button
            variant="primary"
            size="lg"
            block
            disabled={!dayOk}
            loading={extend.isPending}
            onClick={() =>
              extend.mutate(
                { restaurantId: restaurant.id, ends_on: day, reason: reason.trim() },
                { onSuccess: onDismiss },
              )
            }
          >
            Extend it
          </Button>
        </>
      }
    >
      <p className="pf__sub">
        The trial of {trial.plan.name} ends on {formatDay(endsOn, locale)} now. A trial runs at
        most {maxDays} days from today.
      </p>
      {earliest > latest ? (
        <p className="pf__warn">It already runs as long as a trial can.</p>
      ) : (
        <Field
          label="New last day"
          error={
            !dayOk
              ? `Choose a day from ${formatDay(earliest, locale)} to ${formatDay(latest, locale)}.`
              : undefined
          }
        >
          <Input
            type="date"
            value={day}
            min={earliest}
            max={latest}
            onChange={(e) => setDay(e.target.value)}
          />
        </Field>
      )}
      <Field label="Reason" hint="Optional. In the restaurant's audit log.">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <ActionError error={extend.error} action="Extending the trial" />
    </Sheet>
  );
}

export function EndTrialSheet({
  restaurant,
  trial,
  choices,
  onDismiss,
}: {
  restaurant: Restaurant;
  trial: PlatformTrial;
  choices: PlanChoices | undefined;
  onDismiss: () => void;
}) {
  const end = useEndTrial();
  const [reason, setReason] = useState('');
  const trialPlan = choices?.options.find((o) => o.key === trial.plan.key);
  const back = choices?.options.find((o) => o.key === trial.previous_plan.key);
  const lost = trialPlan && back ? featuresLost(trialPlan.features, back.features) : [];

  if (end.data) {
    return (
      <Sheet
        title="Free trial ended"
        onDismiss={onDismiss}
        actions={
          <Button variant="primary" size="lg" block onClick={onDismiss}>
            Done
          </Button>
        }
      >
        <p className="pf__sub">
          {restaurant.name} is back on {end.data.plan.name}, billed {cycleWords(end.data.cycle)}
          .{mailedWords(end.data.mailed)}
        </p>
      </Sheet>
    );
  }

  return (
    <Sheet
      title={`End ${restaurant.name}'s free trial now?`}
      tone="danger"
      onDismiss={onDismiss}
      actions={
        <>
          <Button size="lg" block onClick={onDismiss}>
            Not now
          </Button>
          <Button
            variant="danger"
            size="lg"
            block
            disabled={reason.trim() === ''}
            loading={end.isPending}
            onClick={() => end.mutate({ restaurantId: restaurant.id, reason: reason.trim() })}
          >
            End the trial
          </Button>
        </>
      }
    >
      <p className="pf__sub">
        {restaurant.name} goes back to {trial.previous_plan.name}, billed{' '}
        {cycleWords(trial.previous_cycle)}, at once. The owners are emailed, and see your reason
        in their audit log.
      </p>
      <LostList
        heading={`Back on ${trial.previous_plan.name}, ${restaurant.name} no longer has:`}
        keys={lost}
        names={choices?.feature_names ?? {}}
      />
      <Field label="Reason">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
      <ActionError error={end.error} action="Ending the trial" />
    </Sheet>
  );
}
